import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl, IntlProvider, IntlShape } from 'react-intl';
import { useBrandId } from '@ux/whitelabel';
import { usePrivacyPolicyUrl } from 'domain/selectors/common';
import PrivacyModal from 'presentation/assets/privacy-manager/v1.0/privacy-modal.min';

import PrivacyPolicyDescriptionMessage from './privacy/PrivacyPolicyDescriptionMessage';

const HOSTEUROPE_BRAND_ID = 'b9c8f0f0-60dd-4cab-9da8-512b352d9c1a';

declare global {
  interface Window {
    ENVIRONMENT: string;
  }
}

export default function PrivacyPolicyModal() {
  const intl = useIntl();
  const brandId = useBrandId();
  const privacyPolicyUrl = usePrivacyPolicyUrl();
  const isHostEurope = brandId === HOSTEUROPE_BRAND_ID;
  const managerRef = useRef<{ destroy: () => void } | null>(null);
  const descriptionContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const modalTitle = intl.formatMessage({ id: 'cookie.modal.title' });
    const acceptButtonText = intl.formatMessage({ id: 'cookie.accept' });
    const denyButtonText = intl.formatMessage({ id: 'cookie.decline' });
    const privacyPolicyLabel = intl.formatMessage({ id: 'cookie.privacyPolicy' });
    const supportToggleTitle = intl.formatMessage({ id: 'cookie.toggle.support.title' });
    const supportToggleDescription = intl.formatMessage({ id: 'cookie.toggle.support.text' });
    const showSettingsText = intl.formatMessage({ id: 'cookie.settings.toggle.open' });
    const hideSettingsText = intl.formatMessage({ id: 'cookie.settings.toggle.close' });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderDescription = (_container: HTMLElement): HTMLElement => {
      // Create a wrapper div for React to render into
      const wrapper = document.createElement('div');
      descriptionContainerRef.current = wrapper;
      ReactDOM.render(
        <IntlProvider
          locale={intl.locale}
          messages={(intl as IntlShape & { messages: Record<string, string> }).messages}
        >
          <PrivacyPolicyDescriptionMessage privacyPolicyUrl={privacyPolicyUrl} />
        </IntlProvider>,
        wrapper,
      );
      // Always return the wrapper Node so the library appends it (and never falls back to default)
      return wrapper;
    };

    managerRef.current = PrivacyModal.init({
      theme: 'hosteurope',
      modalTitle,
      renderDescription,
      acceptButtonText,
      denyButtonText,
      showSettingsText,
      hideSettingsText,
      additionalLinks: privacyPolicyUrl
        ? [{ href: privacyPolicyUrl, text: privacyPolicyLabel, target: '_blank', rel: 'noopener noreferrer' }]
        : [],
      supportToggle: {
        title: supportToggleTitle,
        description: supportToggleDescription,
        defaultChecked: false,
      },
      tags: isHostEurope
        ? {
            liveEngage: {
              siteId: '15393996',
              env: window.ENVIRONMENT || 'dev',
            },
          }
        : undefined,
      onAccept: () => {
        // Consent accepted - LiveEngage loaded by library if support enabled
      },
    });

    return () => {
      if (descriptionContainerRef.current) {
        ReactDOM.unmountComponentAtNode(descriptionContainerRef.current);
        descriptionContainerRef.current = null;
      }
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [intl, privacyPolicyUrl, isHostEurope]);

  return null;
}
